import React, { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { SchoolImplementationPlan, VisionAuthorship, VisionGeneration } from '@whizz/api-model';
import { useI18n } from '@whizz/react-i18n';
import NewButton from '../../global/buttons/NewButton';
import { useModal } from '../../hooks/useModal';
import { AddEditGoalModal } from './AddEditGoalModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { GoalCard } from './GoalCard';
import { cohortLabel } from './actionSteps';
import { VisionEditor } from './VisionEditor';
import { CollapsibleCard } from './CollapsibleCard';
import type { VisionDraftApi } from './VisionEditor';
import type { CohortOptions } from './actionSteps';
import { FieldArray, Formik, useFormikContext } from 'formik';
import type { DefaultGoal, GoalModalProps, GoalProps, ImplementationPlanValues } from './types';

interface Props {
  readOnly?: boolean;
  autoGenerateVision?: boolean;
  visionGeneration?: VisionGeneration;
  visionAuthorship?: VisionAuthorship;
  drafts?: VisionDraftApi;
  vision: string;
  goals: GoalProps[];
  defaultGoals: DefaultGoal[];
  yearGroups: { id: number; name: string }[];
  schoolClasses: { id: number; name: string }[];
  schoolTags: { id: number; abbreviation: string }[];
  onSave: (values: ImplementationPlanValues) => void | Promise<unknown>;
}

export function mergePlanValues(current: ImplementationPlanValues, baseline: ImplementationPlanValues, incoming: ImplementationPlanValues): ImplementationPlanValues {
  const visionUnchanged = current.vision === baseline.vision;
  const merged: ImplementationPlanValues = {
    vision: visionUnchanged ? incoming.vision : current.vision,
    goals: JSON.stringify(current.goals) === JSON.stringify(baseline.goals) ? incoming.goals : current.goals,
    autoGenerateVision: current.autoGenerateVision === baseline.autoGenerateVision ? incoming.autoGenerateVision : current.autoGenerateVision,
  };
  // The draft id travels with an unsaved vision change and is dropped once that vision is saved.
  if (!visionUnchanged && undefined !== current.visionDraftId) merged.visionDraftId = current.visionDraftId;
  return merged;
}

const AutoSubmitImplementationForm = ({ server, baseline }: {
  server: ImplementationPlanValues; baseline: MutableRefObject<ImplementationPlanValues>;
}) => {
  const { values, setValues, submitForm, isSubmitting, status } = useFormikContext<ImplementationPlanValues>();
  const previousServer = useRef(JSON.stringify(server));
  useEffect(() => {
    if (isSubmitting) return;
    const snapshot = JSON.stringify(server);
    if (snapshot !== previousServer.current) {
      previousServer.current = snapshot;
      const merged = mergePlanValues(values, baseline.current, server);
      baseline.current = server;
      setValues(merged, false);
      return;
    }
    if (status === 'save-error' || JSON.stringify(values) === JSON.stringify(baseline.current)) return;
    const timeout = window.setTimeout(() => { submitForm().catch(() => undefined); }, 600);
    return () => window.clearTimeout(timeout);
  }, [ server, baseline, values, setValues, submitForm, isSubmitting, status ]);
  return null;
};

const GoalsHeader = ({ action }: { action?: React.ReactNode }): JSX.Element => {
  const { translate: t } = useI18n();
  return (
    <div className={'flex justify-between items-center'}>
      <h3 className={'uppercase text-xs font-semibold tracking-widest text-header'}>{t('pages.IMPLEMENTATION_PLAN.GOALS_AND_METRICS')}</h3>
      {action}
    </div>
  );
};

const ImplementationPlanEditor = ({ readOnly = false, autoGenerateVision = true, visionGeneration, visionAuthorship, drafts, vision, goals, defaultGoals, yearGroups, schoolClasses, schoolTags, onSave }: Props): JSX.Element => {
  const { translate: t } = useI18n();
  const server = { vision, goals, autoGenerateVision };
  const baseline = useRef<ImplementationPlanValues>(server);
  const [ visionOpen, setVisionOpen ] = useState(false);
  const visionCard = (content: React.ReactNode) => (
    <CollapsibleCard title={t('pages.IMPLEMENTATION_PLAN.OUR_VISION')} open={visionOpen} onToggle={() => setVisionOpen((value) => !value)} keepMounted dataCy={'implementation_vision'}>
      <div className={'px-4 pb-4'}>{content}</div>
    </CollapsibleCard>
  );
  const cohorts: CohortOptions = {
    yearGroup: yearGroups,
    class: schoolClasses,
    tag: schoolTags.map((tag) => ({ id: tag.id, name: tag.abbreviation })),
  };
  const cohortLabels = {
    school: t('pages.IMPLEMENTATION_ACTION_STEPS.SCHOOL'),
    unresolved: t('pages.IMPLEMENTATION_ACTION_STEPS.UNRESOLVED_COHORT'),
  };
  const goalTitles = (list: GoalProps[]) => list.map((goal) => t(`pages.ADMIN.IMPACT_REPORTS.GOALS.${goal.goal_key}`));

  const [ addEditModal, toggleAddEditModal, setAddEditModalProps ] = useModal((props: GoalModalProps) => (
    <AddEditGoalModal {...props} />
  ));

  const [ confirmModal, toggleConfirmModal, setConfirmModalProps ] = useModal((props: GoalModalProps) => (
    <DeleteConfirmModal {...props} />
  ));

  const handleAdd = (addGoal: (newGoal: GoalProps) => void) => () => {
    setAddEditModalProps({
      title: t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.NEW_GOAL'),
      data: null,
      defaultGoals,
      yearGroups,
      schoolClasses,
      schoolTags,
      disableMaskClick: true,
      onSuccess: addGoal,
    });

    toggleAddEditModal(true);
  };

  const handleEdit = (editGoal: (goal: GoalProps) => void, removeGoal: () => void) => (data: GoalProps) => {
    setAddEditModalProps({
      title: t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.EDIT_GOAL'),
      mode: 'edit',
      toggleConfirm: toggleConfirmModal,
      data,
      defaultGoals,
      yearGroups,
      schoolClasses,
      schoolTags,
      disableMaskClick: true,
      onSuccess: editGoal,
    });

    setConfirmModalProps({
      title: t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.DELETE_GOAL'),
      toggle: toggleAddEditModal,
      toggleConfirm: toggleConfirmModal,
      data,
      yearGroups: [],
      schoolClasses: [],
      schoolTags: [],
      defaultGoals: [],
      disableMaskClick: true,
      onSuccess: removeGoal,
    });

    toggleAddEditModal(true);
  };

  if (readOnly) {
    return (
      <div className={'space-y-8'}>
        <div className={'space-y-3'}>
          <GoalsHeader />
          {goals.length ? (
            <div className={'grid grid-cols-1 md:grid-cols-3 gap-4'}>
              {goals.map((goal, index) => <GoalCard key={goal.id ?? index} goal={goal} cohortLabel={cohortLabel(goal, cohorts, cohortLabels)} />)}
            </div>
          ) : <p className={'text-gray-500'}>{t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.NO_GOALS')}</p>}
        </div>
        {visionCard(<VisionEditor readOnly hideLabel vision={vision} visionGeneration={visionGeneration} visionAuthorship={visionAuthorship} goalTitles={goalTitles(goals)} />)}
      </div>
    );
  }

  return (
    <>
      {addEditModal}
      {confirmModal}
      <Formik<ImplementationPlanValues>
        initialValues={server}
        onSubmit={async (values, helpers) => {
          try {
            const result = await onSave(values) as SchoolImplementationPlan | undefined;
            const saved = result && typeof result.vision === 'string'
              ? { vision: result.vision, goals: result.goals, autoGenerateVision: result.autoGenerateVision ?? true } : values;
            baseline.current = saved;
            helpers.setValues((current) => mergePlanValues(current, values, saved), false);
            helpers.setStatus(null);
          } catch (error) {
            helpers.setStatus('save-error');
          }
        }}
      >
        {({ handleSubmit, setValues, values, status, isSubmitting }) => (
          <form onSubmit={handleSubmit} className={'space-y-8'}>
            {'save-error' === status && (
              <div role={'alert'}>
                <p>{t('pages.IMPLEMENTATION_ACTION_STEPS.SAVE_ERROR')}</p>
                <button type={'submit'} disabled={isSubmitting}>{t('pages.IMPLEMENTATION_ACTION_STEPS.RETRY')}</button>
              </div>
            )}
            <AutoSubmitImplementationForm server={server} baseline={baseline} />
            <div className={'space-y-3'}>
              <FieldArray name={'goals'}>
                {({ push, replace, remove }) => (
                  <>
                    <GoalsHeader action={(
                      <NewButton
                        type={'button'}
                        onClick={handleAdd((newGoal: GoalProps) => push(newGoal))}
                        disabled={values.goals.length >= 3}
                        data-cy={'implementation-plan-add-goal'}
                      >
                        {t('pages.IMPLEMENTATION_PLAN.ADD_GOAL')}
                      </NewButton>
                    )} />
                    {values.goals?.length ? (
                      <div className={'grid grid-cols-1 md:grid-cols-3 gap-4'}>
                        {values.goals.map((goal, index) => (
                          <GoalCard
                            key={goal.goal_key}
                            goal={goal}
                            cohortLabel={cohortLabel(goal, cohorts, cohortLabels)}
                            handleEdit={handleEdit((editedGoal: GoalProps) => replace(index, editedGoal), () => remove(index))}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className={'text-gray-500'}>{t('pages.ADMIN.IMPACT_REPORTS.GOAL_MODAL.NO_GOALS')}</p>
                    )}
                  </>)}
              </FieldArray>
            </div>
            {visionCard(
              <VisionEditor
                hideLabel
                vision={values.vision}
                visionGeneration={visionGeneration}
                visionAuthorship={visionAuthorship}
                goalTitles={goalTitles(values.goals)}
                drafts={drafts}
                onSaveVision={(nextVision, visionDraftId) => setValues({ ...values, vision: nextVision, visionDraftId })}
              />,
            )}
          </form>
        )}
      </Formik>
    </>
  );
};

export { ImplementationPlanEditor };
